import React from 'react';
import { useTheme } from '@mui/material';

import { UserAvatar } from 'features/profiles/components/user-avatar';
import Stack from 'common/components/material/Stack';
import { Person } from 'features/profiles';
import { EllipsisText } from '../ellipsis-text/ellipsis-text.component';
import AppRoutesEnum from 'common/routes/AppRoutes.enum';
import { useRouter } from 'common/hooks';
import { HoverArrowIcon } from 'common/components/navigation/HoverArrowIcon';
import { LinkTileContainer } from '../link-tile-with-avatar/link-tile-container.component';
import { FeaturesEnum } from 'features/feature-access/features.enum';
import { useFeatureAccess } from 'features/feature-access/hooks/useFeatureAccess';
import useInstancePermissions from 'features/feature-access/hooks/useInstancePermissions';
import { InstancePermissionsResourcesEnum } from 'features/feature-access/enums/InstancePermissions.enum';

type PersonLinkContainerProps = {
  person: Person;
  subtitle?: string | React.ReactElement;
  additionalContent?: React.ReactNode;
  trailingContent?: React.ReactNode;
  showNavigationIndicator?: boolean;
  showBorder?: boolean;
  onClick?: (event: React.MouseEvent) => void;
};

export const PersonLinkContainer: React.FC<PersonLinkContainerProps> = ({
  person,
  subtitle,
  additionalContent,
  trailingContent,
  showNavigationIndicator = true,
  showBorder = false,
  onClick,
}) => {
  const { palette } = useTheme();
  const { getOrgRoute } = useRouter();

  const { canRead: canNavigateToProfile } = useFeatureAccess(FeaturesEnum.OTHER_PROFILE);
  const { permissions } = useInstancePermissions(
    InstancePermissionsResourcesEnum.PROFILE,
    person,
  );

  const route = getOrgRoute(AppRoutesEnum.PROFILE, {
    profileId: person.id,
  });

  const canInteract = canNavigateToProfile && permissions.interact;
  const supportingText = subtitle ?? person.profileHeadline;

  return (
    <LinkTileContainer
      sx={{ p: 0.5, backgroundColor: 'inherit' }}
      route={route}
      showBorder={showBorder}
      onClick={onClick}
      readonly={!canInteract}
    >
      <Stack spacing={1} direction="row" alignItems="center">
        <UserAvatar fullName={person?.fullName} src={person?.photo} size="medium" />
        <Stack minHeight="1.5rem" justifyContent="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <EllipsisText
              className="link-title"
              variant="body4"
              fontWeight="500"
              color={palette.LinkText.Default}
              lineclamp={1}
            >
              {person.fullName}
            </EllipsisText>
            {additionalContent}
          </Stack>
          {React.isValidElement(supportingText) ? (
            supportingText
          ) : supportingText ? (
            <EllipsisText
              className="link-sub-title"
              lineclamp={1}
              variant="caption"
              color={palette.Extras.Extra1}
            >
              {supportingText}
            </EllipsisText>
          ) : null}
        </Stack>
      </Stack>
      {trailingContent}
      {showNavigationIndicator && canInteract && <HoverArrowIcon className="show-on-hover" />}
    </LinkTileContainer>
  );
};
